import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import {
  type LocalTagFormSchema,
  LocalTagsModel,
  type LocalUpdateTagMutationParamsSchema,
} from '@/features/tag/models';
import { queryKeyMap } from '@/features/tag/utils';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';

export const useLocalTags = <
  T extends object,
  U = z.infer<typeof LocalTagsModel>,
>({
  params,
  select,
}: {
  params?: T;
  select?: (data: z.infer<typeof LocalTagsModel>) => U;
} = {}) => {
  return useQuery({
    queryKey: queryKeyMap.localTags(params),
    queryFn: async () => {
      const res = await axiosGet('/api/tags/local/', params);
      return LocalTagsModel.parse(res);
    },
    select,
  });
};

export const useCreateLocalTag = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (values: z.infer<typeof LocalTagFormSchema>) => {
      const {
        organizationId,
        period,
        nestedSharedOrganizationIds,
        ...payload
      } = values;
      return await axiosPost('/api/tags/local/', {
        ...payload,
        organizationId: organizationId.at(-1),
        sharedOrganizationIds: nestedSharedOrganizationIds.map((v) => v.at(-1)),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(queryKeyMap.tags()),
        invalidate(queryKeyMap.localTags()),
      ]);
    },
  });
};

export const useUpdateLocalTag = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof LocalUpdateTagMutationParamsSchema>,
    ) => {
      const {
        id,
        organizationId,
        period,
        nestedSharedOrganizationIds,
        ...payload
      } = values;
      return await axiosPatch(`/api/tags/local/${id}/`, {
        ...payload,
        organizationId: organizationId.at(-1),
        sharedOrganizationIds: nestedSharedOrganizationIds.map((v) => v.at(-1)),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(queryKeyMap.tags()),
        invalidate(queryKeyMap.localTags()),
      ]);
    },
  });
};

export const useDeleteLocalTag = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`/api/tags/local/${id}/`);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(queryKeyMap.tags()),
        invalidate(queryKeyMap.localTags()),
      ]);
    },
  });
};
